import { Close } from '@mui/icons-material';
import { Box, CardContent, CardProps, IconButton, Stack, Typography } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Location } from 'react-router-dom';

import AnimatedCircularProgress, { AnimatedCircularProgressRef } from './AnimatedCircularProgress';
import { UploadInProgressView } from './UploadInProgressView';
import { UploaderLocationState } from '../uploader/Uploader';

import HauserIcon from '@/assets/images/hauser-icon.svg?react';
import { CompletionView } from '@/components/common/CompletionView';
import { FilePickerWithList } from '@/components/common/FilePickerWithList';
import { FixedCard } from '@/components/common/FixedCard';
import { useAuthContext } from '@/context/AuthProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useUploaderContext } from '@/context/UploaderProvider';
import { useLogger } from '@/hooks/useLogger';
import { Document } from '@/models/Document';
import { useTranslationPrefix } from '@/translations/i18n';

export interface UploaderCardProps extends CardProps {
  disableDropZone?: boolean;
  disableRedirect?: boolean;
}

const MAX_UPLOAD_ANIMATION_DURATION = 30 * 1000;

export const UploaderCard = ({ ...rest }: UploaderCardProps) => {
  const { user } = useAuthContext();
  const location = useLocation() as Location<UploaderLocationState | undefined>;

  const { properties } = usePropertiesContext();
  const {
    uploadedDocuments,
    removeUploadedDocument,
    startFileUpload,
    stepProcessing,
    streamingProperty,
    streamingIssuesCount,
  } = useUploaderContext();
  const uploadProgressRef = useRef<AnimatedCircularProgressRef>(null);

  const navigate = useNavigate();
  const _t = useTranslationPrefix('uploader');
  const logger = useLogger('UploaderCard');

  // Redirect to dashboard when processing starts (for users with existing properties)
  const hasRedirectedRef = useRef(false);
  useEffect(() => {
    if (
      stepProcessing >= 1 &&
      !hasRedirectedRef.current &&
      (properties ?? []).length > 0 &&
      location.pathname === '/uploader'
    ) {
      hasRedirectedRef.current = true;
      navigate('/dashboard/summary');
    }
    if (stepProcessing === 0) {
      hasRedirectedRef.current = false;
    }
  }, [stepProcessing, properties, location.pathname]);

  const handleFilesDrop = (files: File[]) => {
    logger.debug(`dropped files: ${JSON.stringify(files)}`);

    // filter out files that are already uploaded based on filename
    const allowedFiles = files.filter(
      (file) => !uploadedDocuments.some((uploaded) => uploaded.filename === file.name),
    );

    if (allowedFiles.length === 0) {
      logger.debug('all dropped files are already uploaded');
      return;
    }

    startFileUpload(allowedFiles);
  };

  const handleRemoveDocument = (document: Document) => {
    removeUploadedDocument(document);
  };

  const handleClose = () => {
    navigate('/');
  };

  useEffect(() => {
    if (stepProcessing === 4) {
      uploadProgressRef.current?.complete();
    }
  })

  // Log when streaming property changes
  useEffect(() => {
    if (streamingProperty) {
      logger.debug(`Streaming property updated: ${streamingProperty}`);
    }
    if (streamingIssuesCount) {
      logger.debug(`Streaming number of issues updated: ${streamingIssuesCount.moderate}`);
    }
  }, [streamingProperty, streamingIssuesCount]);

  return (
    <FixedCard {...rest}>
      {(properties ?? []).length > 0 && location.pathname === '/uploader' && (
        <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
          <IconButton color="primary">
            <Close fontSize="inherit" onClick={handleClose} />
          </IconButton>
        </Box>
      )}
      <CardContent
        sx={{
          gap: 10,
          alignItems: 'center',
          pb: 7,
        }}>
        <HauserIcon width={88} height={88} />
        {stepProcessing >= 0 && stepProcessing < 4 && (
          <Heading
            userName={user?.firstName || ''}
            processingStep={stepProcessing}
          />
        )}

        {stepProcessing === 0 && (
          <FilePickerWithList
            flexGrow={0}
            maxFileCount={(properties ?? []).length === 0 ? 1 : undefined}
            onFilesDrop={handleFilesDrop}
            uploadedFiles={uploadedDocuments}
            onDelete={handleRemoveDocument}
          />
        )}

        {stepProcessing > 0 && stepProcessing < 4 && (
          <AnimatedCircularProgress
            ref={uploadProgressRef}
            size={180}
            animationDuration={MAX_UPLOAD_ANIMATION_DURATION}
          />
        )}
        {/* Completion View - Shown when processing is complete */}
        {stepProcessing === 4 && <UploadInProgressView />}
        {stepProcessing === 5 && <CompletionView />}

      </CardContent>
    </FixedCard>
  );
};

const Heading = ({
  userName,
  processingStep = 0,
}: {
  userName: string;
  processingStep: number;
}) => {
  const t = useTranslationPrefix('uploader');

  const initialText = t('intro_text.initial', { returnObjects: true, name: userName });
  const [currentText, setCurrentText] = useState(initialText);
  const [headerTitle, setHeaderTitle] = useState<string | null>(null);

  const updateCurrentText = () => {
    if (processingStep !== 0) {
      setHeaderTitle(t('step_title.' + processingStep));
      setCurrentText(t('step_subtitle.' + processingStep));
    }
  };

  useEffect(() => {
    // Update the text as soon as one of the flags changes
    updateCurrentText();
  }, [processingStep, headerTitle, currentText]);

  return (
    <Stack alignItems="center" sx={{ textAlign: 'center', opacity: 0.75 }}>
      {headerTitle && (
        <Typography variant="h4" fontWeight="600" lineHeight={1.35} sx={{ mb: 2 }}>
          {headerTitle}
        </Typography>
      )}
      <Typography variant="h6" fontWeight="regular" lineHeight={1.35}>
        {Array.isArray(currentText)
          ? currentText.map((paragraph, index) => (
              <Typography key={index} variant="inherit">
                {paragraph}
              </Typography>
            ))
          : currentText}
      </Typography>
    </Stack>
  );
};
